// The camera stream and everything that keeps it alive. getUserMedia needs a
// user gesture the first time on iOS; re-acquiring later (after the screen
// slept, or the track ended) is attempted automatically and falls back to the
// error screen if iOS refuses.
import { config } from '../config.js';
import { Emitter } from '../util/events.js';

// status: idle | starting | active | ended | denied | unsupported | error
export class CameraStream extends Emitter {
  constructor({ log = () => {} } = {}) {
    super();
    this.log = log;
    this.status = 'idle';
    this.stream = null;
    this.track = null;
    this.lastError = null;
    this.everStarted = false;
    this.lastTime = -1;
    this.stalledSince = null;
    this.lastCheck = 0;
    this.recoveries = 0;
    this.lastDims = null;
    this.initialSettings = null;
    this.lastTick = 0;
    this.resuming = false;

    // playsinline + muted, or iOS hijacks the stream into its fullscreen
    // player. Kept in the DOM (invisible) because iOS is happier that way.
    const video = document.createElement('video');
    video.setAttribute('playsinline', '');
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    Object.assign(video.style, {
      position: 'fixed',
      left: '0',
      top: '0',
      width: '2px',
      height: '2px',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.append(video);
    this.video = video;

    const onReturn = (what) => {
      this.log(`${what} (visibility ${document.visibilityState})`);
      if (document.visibilityState !== 'visible') return;
      if (!this.everStarted || this.status === 'denied') return;
      this.onResumed(what);
    };
    document.addEventListener('visibilitychange', () => onReturn('visibilitychange'));
    window.addEventListener('pageshow', (e) => onReturn(`pageshow${e.persisted ? ' (bfcache)' : ''}`));
    window.addEventListener('focus', () => onReturn('focus'));
  }

  get supported() {
    return Boolean(navigator.mediaDevices?.getUserMedia);
  }

  async start() {
    if (!this.supported) {
      this.status = 'unsupported';
      this.lastError = new Error('getUserMedia unavailable (insecure context or old browser)');
      this.log(this.lastError.message, 'error');
      this.emit('error', { kind: 'unsupported', error: this.lastError });
      return false;
    }
    this.status = 'starting';
    this.emit('status', this.status);
    const constraints = {
      audio: false,
      video: {
        facingMode: { ideal: config.camera.facingMode },
        width: { ideal: config.camera.idealWidth },
        height: { ideal: config.camera.idealHeight },
        frameRate: { ideal: config.camera.idealFrameRate },
        aspectRatio: { ideal: config.camera.idealWidth / config.camera.idealHeight },
      },
    };
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      await this.#attach(stream);
      this.everStarted = true;
      return true;
    } catch (error) {
      this.lastError = error;
      const denied = error.name === 'NotAllowedError' || error.name === 'SecurityError';
      const missing = error.name === 'NotFoundError' || error.name === 'OverconstrainedError';
      this.status = denied ? 'denied' : missing ? 'unsupported' : 'error';
      this.log(`camera ${this.status}: ${error.name} ${error.message}`, 'error');
      this.emit('status', this.status);
      this.emit('error', { kind: this.status, error });
      return false;
    }
  }

  async #attach(stream) {
    this.#release();
    this.stream = stream;
    this.track = stream.getVideoTracks()[0];
    this.track.addEventListener('ended', () => {
      if (this.track?.readyState === 'ended' && this.status === 'active') {
        this.status = 'ended';
        this.emit('status', this.status);
        this.log('video track ended');
        this.ensureAlive('track ended');
      }
    });
    this.video.srcObject = stream;
    try {
      await this.video.play();
    } catch (error) {
      // Muted inline playback should never be refused, but log it if it is.
      this.log(`video.play(): ${error.name} ${error.message}`, 'error');
    }
    if (this.video.readyState < 2) {
      await new Promise((resolve) => this.video.addEventListener('loadeddata', resolve, { once: true }));
    }
    this.status = 'active';
    this.lastTime = -1;
    this.stalledSince = null;
    const s = this.track.getSettings();
    const dims = `${this.video.videoWidth}x${this.video.videoHeight}`;
    if (this.lastDims && this.lastDims !== dims) {
      this.log(`camera mode changed: ${this.lastDims} -> ${dims}; calibration assumes ${this.lastDims}`, 'error');
    }
    this.lastDims = dims;
    this.initialSettings = { ...s };
    this.log(`camera active ${dims} (${s.width}x${s.height}@${s.frameRate ?? '?'} ${s.facingMode ?? ''})`);
    this.emit('status', this.status);
    this.emit('active', { width: this.video.videoWidth, height: this.video.videoHeight, settings: s });
  }

  // iOS can resume a capture session with different settings (seen as a
  // zoomed-in picture). Log what changed and try to restore zoom.
  async #checkSettingsAfterResume(reason) {
    if (!this.track || !this.initialSettings) return;
    const now = this.track.getSettings();
    const keys = ['width', 'height', 'zoom', 'aspectRatio', 'frameRate', 'focusDistance'];
    const changed = keys.filter((k) => now[k] !== undefined && now[k] !== this.initialSettings[k]);
    if (changed.length === 0) {
      this.log(`settings unchanged after ${reason} (zoom ${now.zoom ?? 'n/a'})`);
      return;
    }
    this.log(`settings changed after ${reason}: ${changed.map((k) => `${k} ${this.initialSettings[k]} -> ${now[k]}`).join(', ')}`, 'error');
    if (changed.includes('zoom')) {
      try {
        await this.track.applyConstraints({ advanced: [{ zoom: this.initialSettings.zoom }] });
        this.log(`zoom reset to ${this.initialSettings.zoom}: now ${this.track.getSettings().zoom}`);
      } catch (error) {
        this.log(`zoom reset failed: ${error.name} ${error.message}`, 'error');
      }
    }
  }

  /** Manual: drop the stream and negotiate a new one (overlay button). */
  reacquire() {
    return this.ensureAlive('manual', { force: true });
  }

  /** Experiment: release the stream, wait for WebKit to tear the session down, then re-request. */
  async reacquireDelayed(ms = 2000) {
    this.log(`releasing camera, waiting ${ms} ms`);
    this.#release();
    this.status = 'ended';
    await new Promise((r) => setTimeout(r, ms));
    this.recoveries++;
    return this.start();
  }

  /** Experiment: force the capture session to reconfigure by changing resolution and back. */
  async cycleResolution() {
    if (!this.track) return;
    try {
      await this.track.applyConstraints({ width: { ideal: 1280 }, height: { ideal: 960 } });
      const a = this.track.getSettings();
      this.log(`cycled down: ${a.width}x${a.height}`);
      await new Promise((r) => setTimeout(r, 500));
      await this.track.applyConstraints({ width: { ideal: config.camera.idealWidth }, height: { ideal: config.camera.idealHeight } });
      const b = this.track.getSettings();
      this.log(`cycled up: ${b.width}x${b.height}`);
      this.emit('active', { width: this.video.videoWidth, height: this.video.videoHeight, settings: b });
    } catch (error) {
      this.log(`cycle failed: ${error.name} ${error.message}`, 'error');
    }
  }

  #release() {
    if (this.stream) for (const t of this.stream.getTracks()) t.stop();
    this.stream = null;
    this.track = null;
    this.video.srcObject = null;
  }

  stop() {
    this.#release();
    this.status = 'idle';
    this.emit('status', this.status);
  }

  // Resume the existing stream if it is merely paused (iOS pauses the video
  // element while backgrounded); only negotiate a new stream when the track
  // has ended or frames refuse to advance. A second getUserMedia can come
  // back in a different capture mode, which breaks the calibration.
  async ensureAlive(reason, { force = false } = {}) {
    if (this.status === 'starting') return;
    const live = this.track && this.track.readyState === 'live';
    if (live && !force) {
      if (this.video.paused) {
        try {
          await this.video.play();
          this.log(`video resumed (${reason})`);
        } catch (error) {
          this.log(`video.play() on resume: ${error.name}`, 'error');
        }
      }
      if (!this.video.paused) {
        this.stalledSince = null;
        this.lastTime = -1;
        await this.#checkSettingsAfterResume(reason);
        return; // the watchdog re-checks frame advance once a second
      }
    }
    this.recoveries++;
    this.log(`re-acquiring camera (${reason})`);
    this.start();
  }

  // iOS resumes the capture session zoomed in after the app has been in the
  // background (2026-09-19, iPad 11 / iPadOS 26); a plain re-acquire does not
  // clear it, a release + 2 s pause + re-acquire does. Called from the
  // return events and from the frame-gap detector below, whichever fires.
  async onResumed(reason) {
    if (this.status === 'starting' || this.resuming) return;
    if (!this.everStarted || this.status === 'denied') return;
    this.resuming = true;
    try {
      this.log(`resumed (${reason}): re-acquiring with a pause to clear the iOS zoom`);
      await this.reacquireDelayed(config.camera.resumeReleaseMs);
    } finally {
      this.resuming = false;
    }
  }

  // Called once per frame; does its checks once per second.
  tick(now) {
    // No frames for a while means the page was suspended (background, lock
    // screen). Return events are unreliable in a Home Screen app, this is not.
    if (this.lastTick && now - this.lastTick > config.camera.suspendGapMs) {
      const gap = ((now - this.lastTick) / 1000).toFixed(1);
      this.lastTick = now;
      this.onResumed(`frame gap ${gap} s`);
      return;
    }
    this.lastTick = now;
    if (this.status !== 'active' || now - this.lastCheck < 1000) return;
    this.lastCheck = now;
    if (document.visibilityState !== 'visible') return;
    if (this.track?.readyState !== 'live') {
      this.ensureAlive('track not live', { force: true });
      return;
    }
    const t = this.video.currentTime;
    if (t === this.lastTime) {
      this.stalledSince ??= now;
      if (now - this.stalledSince >= 3000) {
        this.ensureAlive('frames stalled', { force: true });
        this.stalledSince = null;
      }
    } else {
      this.stalledSince = null;
    }
    this.lastTime = t;
  }

  getInfo() {
    const settings = this.track?.getSettings?.() ?? {};
    let capabilities = {};
    try {
      capabilities = this.track?.getCapabilities?.() ?? {};
    } catch {
      /* not supported */
    }
    return {
      status: this.status,
      width: this.video.videoWidth,
      height: this.video.videoHeight,
      readyState: this.video.readyState,
      paused: this.video.paused,
      settings,
      capabilities,
      recoveries: this.recoveries,
      error: this.lastError ? `${this.lastError.name}: ${this.lastError.message}` : '',
    };
  }
}
